/**
 * CloudListItem - 云端标签/标签组列表项
 * 用于广场页展示公开的 CloudTag/CloudTagGroup
 * 直接接收 CloudPublicTagGroup/CloudPublicTag 数据
 */

import React from 'react';
import { HashIcon } from 'lucide-react';
import { CaretDownIcon, CaretRightIcon, VaultIcon } from '@phosphor-icons/react';
import { enhanceSubscriberCount } from '../utils/postList';
import type { CloudPublicTagGroup, CloudPublicTag } from '../apiClient';

export interface CloudListItemProps {
  /** 类型：标签组或独立标签 */
  type: 'tagGroup' | 'tag';
  /** 标签组数据 */
  tagGroup?: CloudPublicTagGroup;
  /** 标签数据 */
  tag?: CloudPublicTag;
  /** 封面图片（异步加载） */
  topImages?: string[];
  /** 博主头像（异步加载） */
  topBloggers?: Array<{ nickname: string; avatarUrl: string | null }>;
  /** 图片是否正在加载（用于显示骨架屏） */
  imagesLoading?: boolean;
  /** 列表项索引（用于错开淡入动画） */
  itemIndex?: number;
  /** 是否选中 */
  isSelected?: boolean;
  /** 是否展开（仅 tagGroup） */
  isExpanded?: boolean;
  /** 子标签列表（仅 tagGroup） */
  tags?: string[];
  /** 当前选中的子标签 */
  selectedCloudTag?: { groupId: string; tagName: string } | null;
  /** 已验证密码的 item ID 映射（用于恢复加密 TagGroup 的展开功能） */
  verifiedItems?: Map<string, string>;
  /** 是否有更新（显示红点） */
  hasUpdate?: boolean;
  /** 点击回调 */
  onClick?: () => void;
  /** 展开/折叠回调（仅 tagGroup） */
  onToggleExpand?: () => void;
  /** 选择子标签回调（仅 tagGroup） */
  onSelectTag?: (tagName: string) => void;
}

/**
 * CloudListItem 组件
 * 用于展示公开的 CloudTagGroup/CloudTag 列表项
 */
export const CloudListItem: React.FC<CloudListItemProps> = ({
  type,
  tagGroup,
  tag,
  topImages = [],
  imagesLoading = false,
  itemIndex = 0,
  isSelected = false,
  isExpanded = false,
  tags = [],
  selectedCloudTag,
  verifiedItems,
  hasUpdate = false,
  onClick,
  onToggleExpand,
  onSelectTag,
}) => {
  // 获取当前项目的数据
  const item = type === 'tagGroup' ? tagGroup : tag;
  if (!item || !item.id) return null;

  // 获取显示名称（优先使用 alias）
  const displayName = (item as any).alias || item.name || '无标题';

  // 获取描述（加密时覆盖为提示文字）
  const displayDescription = item.isEncrypted ? '这是一个加密收藏夹' : item.description;

  // 获取 uploader 名称（匿名时显示"匿名用户"）
  const uploaderName = item.isAnonymous ? '匿名用户' : item.uploader?.username || '匿名用户';

  return (
    <>
      {/* 骨架屏和淡入动画样式 */}
      <style>{`
        @keyframes skeleton-pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
      <div style={{ marginBottom: 2 }}>
      {/* 主项目 */}
      <div
        onClick={onClick}
        style={{
          padding: '12px 12px 12px 18px',
          cursor: 'pointer',
          backgroundColor: isSelected ? 'var(--ant-color-fill-secondary)' : 'transparent',
          borderRadius: 8,
          position: 'relative',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = 'var(--ant-color-fill-tertiary)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        {/* 展开箭头 (仅 TagGroup 且非加密，或已验证密码) */}
        {type === 'tagGroup' && onToggleExpand && (!item.isEncrypted || verifiedItems?.has(item.id)) && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            style={{
              position: 'absolute',
              left: 1,
              top: 22,
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              fontSize: 10,
              color: 'var(--ant-color-text-tertiary)',
              padding: 2,
            }}
          >
            {isExpanded ? <CaretDownIcon size={12} /> : <CaretRightIcon size={12} />}
          </span>
        )}

        {/* Tag 图标 */}
        {type === 'tag' && (
          <span
            style={{
              position: 'absolute',
              left: 4,
              top: 22,
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              fontSize: 12,
              color: 'var(--ant-color-text-tertiary)',
            }}
          >
            <HashIcon size={12} />
          </span>
        )}

        {/* 主要内容 */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          {/* 左侧文字信息 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontWeight: 500,
              marginBottom: 2,
              color: 'var(--ant-color-text)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              {displayName}
              {item.isEncrypted && (
                <VaultIcon size={14} style={{ color: 'var(--ant-color-text-tertiary)' }} />
              )}
              {hasUpdate && !isSelected && (
                <span style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: 'var(--ant-color-error)',
                  flexShrink: 0,
                }} />
              )}
            </div>

            {displayDescription && (
              <div style={{
                fontSize: 12,
                color: 'var(--ant-color-text-tertiary)',
                marginTop: 0,
                marginBottom: 4,
                lineHeight: 1.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {displayDescription}
              </div>
            )}

            <div style={{
              fontSize: 12,
              color: 'var(--ant-color-text-tertiary)',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 4,
            }}>
              <span>
                {uploaderName} · {item.postCount || 0} 帖 · {enhanceSubscriberCount(item.subscriberCount || 0, item.id)} 订阅
              </span>
            </div>
          </div>

          {/* 右侧 Top 3 封面图预览（加密时不显示） */}
          {/* 骨架屏占位（加载中显示） */}
          {imagesLoading && !item.isEncrypted && (
            <div style={{
              display: 'flex',
              position: 'relative',
              height: 48,
              width: 52,
              flexShrink: 0,
            }}>
              {[0, 1, 2].map((index) => (
                <div
                  key={index}
                  style={{
                    width: 36,
                    height: 48,
                    borderRadius: 4,
                    position: 'absolute',
                    left: index * 10,
                    zIndex: 3 - index,
                    backgroundColor: 'var(--ant-color-fill-secondary)',
                    border: '1.2px solid var(--ant-layout-body-bg)',
                    transform: `perspective(160px) rotateY(-20deg) scale(${index === 0 ? 1 : index === 1 ? 0.96 : 0.90})`,
                    animation: 'skeleton-pulse 1.8s ease-in-out infinite',
                  }}
                />
              ))}
            </div>
          )}
          {/* 实际图片（加载完成后淡入显示） */}
          {!imagesLoading && topImages && topImages.length > 0 && !item.isEncrypted && (
            <div style={{
              display: 'flex',
              position: 'relative',
              height: 48,
              width: 36 + (topImages.length - 1) * 8,
              flexShrink: 0,
              opacity: 0,
              animation: `fadeIn 0.3s ease-in ${itemIndex * 50}ms forwards`,
            }}>
              {topImages.map((imageUrl: string, index: number) => (
                <img
                  key={index}
                  src={imageUrl}
                  alt={`preview-${index}`}
                  style={{
                    width: 36,
                    height: 48,
                    borderRadius: 4,
                    objectFit: 'cover',
                    position: 'absolute',
                    left: index * 10,
                    zIndex: 3 - index,
                    border: '1.2px solid var(--ant-layout-body-bg)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    transform: `perspective(160px) rotateY(-20deg) scale(${index === 0 ? 1 : index === 1 ? 0.96 : 0.90})`,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 子标签列表 (仅展开时显示) */}
      {type === 'tagGroup' && isExpanded && tags.length > 0 && onSelectTag && (
        <div style={{ paddingLeft: 28, marginTop: 4 }}>
          {tags.map((tagName) => {
            const isTagSelected = selectedCloudTag?.groupId === item.id && selectedCloudTag?.tagName === tagName;
            return (
              <div
                key={tagName}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectTag(tagName);
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  marginBottom: 2,
                  cursor: 'pointer',
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  backgroundColor: isTagSelected ? 'var(--ant-color-fill-secondary)' : 'transparent',
                  color: isTagSelected ? 'var(--ant-color-text)' : 'var(--ant-color-text-secondary)',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!isTagSelected) {
                    e.currentTarget.style.backgroundColor = 'var(--ant-color-fill-tertiary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isTagSelected) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <HashIcon size={12} />
                {tagName}
              </div>
            );
          })}
        </div>
      )}
    </div>
    </>
  );
};

export default CloudListItem;
